import { ReportOptionDetails } from '../projects/generic/ReportGenerationV2/utils';
import type { AttributionReportIncludeOptions, DependencyInfoOption } from '../../../shared/reports';

type IncludeBooleanOption = {
  kind: 'includeBoolean';
  id: keyof AttributionReportIncludeOptions;
  label: string;
  tooltip: string;
};

type DependencyInfoGroupOption = {
  kind: 'dependencyInfoGroup';
  id: string;
  label: string;
  tooltip: string;
  dependencyInfoOptions: readonly DependencyInfoOption[];
};

type StandardReportOption = IncludeBooleanOption | DependencyInfoGroupOption;

/**
 * Single source of truth for standard report options. Each entry defines
 * UI display data (label, tooltip) and carries the information needed to
 * build backend query params.
 *
 * For 'includeBoolean' entries, the `id` doubles as both the React element
 * key and the backend query parameter name (e.g. 'includeDependencySummary').
 * For 'dependencyInfoGroup' entries, `id` is a UI-only identifier and the
 * backend params come from the `dependencyInfoOptions` array.
 */
export const STANDARD_REPORT_OPTIONS: readonly StandardReportOption[] = [
  {
    kind: 'includeBoolean',
    id: 'includeDependencySummary',
    label: 'Dependencies Summary',
    tooltip: ReportOptionDetails.includeDependencySummary.description,
  },
  {
    kind: 'includeBoolean',
    id: 'includeDirectDependencies',
    label: 'Direct Dependencies',
    tooltip: ReportOptionDetails.includeDirectDependencies.description,
  },
  {
    kind: 'includeBoolean',
    id: 'includeConcludedLicenses',
    label: 'Concluded License(s)',
    tooltip: ReportOptionDetails.includeConcludedLicenses.description,
  },
  {
    kind: 'includeBoolean',
    id: 'includeCopyrightList',
    label: 'Copyright',
    tooltip: ReportOptionDetails.includeCopyrightList.description,
  },
  {
    kind: 'includeBoolean',
    id: 'includeNoticeFiles',
    label: 'Notice File(s)',
    tooltip: ReportOptionDetails.includeNoticeFiles.description,
  },
  // Note that this doesn't map directly to a single backend option, but rather a bunch of dependency info options.
  // We only show it as a single checkbox for simplicity.
  {
    kind: 'dependencyInfoGroup',
    id: 'packageMetadata',
    label: 'Package Metadata',
    tooltip: 'Includes per-dependency details such as authors, description, licenses, package manager, homepage, download URL, dependency paths, and copyrights.',
    dependencyInfoOptions: [
      'Library',
      'Authors',
      'Description',
      'ConcludedLicense',
      'License',
      'OtherLicenses',
      'Source',
      'ProjectUrl',
      'PackageDownloadUrl',
      'DependencyPaths',
      'Copyrights',
    ],
  },
];
